import { Button } from '@/components/design-system/Button';
import { ProductCard } from '@/components/screens/ShopPage/components';
import { FormulaProduct } from '@viome/viome-base/src/usecase/Shop/ShopQuery';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import OfferBannerWithTimer from '@screens/ShopPage/components/OfferBannerWithTimer';
import { PriceDiscountSheet } from '@screens/ShopPage/components/PriceDiscountSheet';

export type PriceInfo = {
  greenDaily: string;
  displayMonthlyPrice: string;
  strikethroughDaily: string | null;
  isPriceAvailable: boolean;
  hasDiscount: boolean;
  monthlyPriceWithOutDiscount: string;
  offerDuration?: string;
};

export const PriceDisplay = ({ priceInfo }: { priceInfo: PriceInfo }) => {
  if (!priceInfo.isPriceAvailable) {
    return null;
  }
  return (
    <div className="flex items-baseline">
      {priceInfo.hasDiscount && priceInfo.strikethroughDaily && (
        <span className="typography-h2 line-through mr-1 text-secondary-foreground">
          ${priceInfo.strikethroughDaily}
        </span>
      )}
      <span className="typography-h2 text-[#146464]">
        ${priceInfo.greenDaily}
      </span>
      <span className="typography-headline2 text-[#146464]">/day</span>
    </div>
  );
};

export const MonthlyPriceDisplay = ({
  priceInfo,
}: {
  priceInfo: PriceInfo;
}) => {
  if (!priceInfo.isPriceAvailable) return null;
  return (
    <p className="typography-body-sm -mt-4 mb-4">
      <span className="text-tertiary">
        (${priceInfo.displayMonthlyPrice}/mo)
      </span>{' '}
      <PriceDiscountSheet priceInfo={priceInfo} />
    </p>
  );
};

interface CustomizeModeViewProps {
  handleBackClick: () => void;
  nonSubscribedProducts: FormulaProduct[];
  selectedProductIds: Set<string>;
  handleToggleProductSelected: (productId: string) => void;
  handleSelectAll: () => void;
  dynamicPricing: PriceInfo;
  ctaButtonLabel: string;
  onCtaClick: () => void;
  isCtaLoading: boolean;
}

export const CustomizeModeView = ({
  handleBackClick,
  nonSubscribedProducts,
  selectedProductIds,
  handleToggleProductSelected,
  handleSelectAll,
  dynamicPricing,
  ctaButtonLabel,
  onCtaClick,
  isCtaLoading,
}: CustomizeModeViewProps) => {
  const savings =
    dynamicPricing?.hasDiscount &&
    dynamicPricing.strikethroughDaily &&
    dynamicPricing.greenDaily
      ? parseFloat(dynamicPricing.strikethroughDaily) -
        parseFloat(dynamicPricing.greenDaily)
      : 0;
  return (
    <div className="container mx-auto p-4 md:p-8">
      <Button
        variant="ghost"
        onClick={handleBackClick}
        className="mb-6 text-primary hover:text-primary-focus"
      >
        <ChevronLeft className="w-5 h-5 mr-2" />
        Back
      </Button>
      <div className="flex flex-col md:flex-row gap-8 lg:gap-16">
        <div className="md:w-7/12 lg:w-8/12 space-y-6">
          <h1 className="typography-h1 text-primary mb-2">
            Choose your products
          </h1>
          <div className="space-y-4">
            {nonSubscribedProducts?.map((formula: FormulaProduct) => (
              <ProductCard
                key={formula.product_id}
                formula={formula}
                isSelected={selectedProductIds.has(String(formula.product_id))}
                onToggleSelected={handleToggleProductSelected}
                isEditable={true}
                cardStyle="default"
              />
            ))}
          </div>
          {nonSubscribedProducts.length > 0 && (
            <Button
              variant="link"
              onClick={handleSelectAll}
              className="text-success hover:text-[#027B63] p-0 typography-body-sm w-full justify-end pr-5"
            >
              {savings > 0
                ? `Select All and Save $${savings.toFixed(2)}/day`
                : 'Select All'}
            </Button>
          )}
        </div>
        <div className="md:w-5/12 lg:w-4/12 space-y-6">
          <div className="flex items-baseline gap-2 flex-wrap">
            <PriceDisplay priceInfo={dynamicPricing} />
            <MonthlyPriceDisplay priceInfo={dynamicPricing} />
          </div>
          <div className="fixed bottom-16 md:bottom-auto md:relative md:left-auto left-0 w-full flex justify-center md:block z-50">
            <Button
              className="w-full rounded-md py-3 bg-black text-white hover:bg-opacity-90 typography-btn-medium md:min-w-0 min-w-[300px] px-24 md:px-5"
              onClick={onCtaClick}
              disabled={
                isCtaLoading ||
                (!dynamicPricing.isPriceAvailable &&
                  selectedProductIds.size === 0)
              }
            >
              {isCtaLoading ? 'Processing...' : ctaButtonLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface AllSubscribedViewProps {
  products: FormulaProduct[];
}

export const AllSubscribedView = ({ products }: AllSubscribedViewProps) => {
  return (
    <div className="container flex flex-col md:flex-row gap-8 p-4">
      <div className="md:w-5/12 flex flex-col">
        <h1 className="typography-h1 md:typography-display2 text-primary mb-2 text-center md:text-start">
          You are taking full advantage of our great products.
        </h1>
      </div>
      <div className="max-w-md mx-auto md:w-7/12">
        <h2 className="typography-h3 text-primary font-medium mb-4">
          Formulas in Your Plan
        </h2>
        {products.map((formula: FormulaProduct) => (
          <ProductCard
            key={formula.product_id}
            formula={formula}
            isSelected={true}
            isEditable={false}
            cardStyle="subscribedHighlight"
          />
        ))}
      </div>
    </div>
  );
};

interface AnySubscribedViewProps {
  pricingForUpgrade: PriceInfo;
  ctaButtonLabel: string;
  nonSubscribedProducts: FormulaProduct[];
  products: FormulaProduct[];
  onCtaClick: () => void;
  isCtaLoading: boolean;
}

export const AnySubscribedView = ({
  pricingForUpgrade,
  ctaButtonLabel,
  nonSubscribedProducts,
  products,
  onCtaClick,
  isCtaLoading,
}: AnySubscribedViewProps) => {
  const subscribedProducts = products.filter((p) => p.is_subscribed);

  return (
    <div className="container flex flex-col md:flex-row gap-8 p-4">
      <div className="md:w-5/12">
        <h1 className="typography-h1 md:typography-display2 text-primary mb-2 text-center md:text-left">
          Maximize the benefits of your nutrition plan
        </h1>
        {pricingForUpgrade.isPriceAvailable && (
          <>
            <p className="typography-body text-secondary mb-4">
              Upgrade for an additional
            </p>
            <div className="flex items-baseline gap-2 flex-wrap">
              <PriceDisplay priceInfo={pricingForUpgrade} />
              <MonthlyPriceDisplay priceInfo={pricingForUpgrade} />
            </div>
          </>
        )}
        <div className="fixed bottom-16 md:bottom-auto md:relative md:left-auto left-0 w-full flex justify-center md:block z-50">
          <Button
            className="rounded-md py-3 bg-black text-white hover:bg-opacity-90 typography-btn-medium mt-4 md:min-w-0 min-w-[300px] px-24 md:px-5"
            onClick={onCtaClick}
            disabled={isCtaLoading}
          >
            {isCtaLoading ? 'Processing...' : ctaButtonLabel}
          </Button>
        </div>
      </div>
      <div className="max-w-md mx-auto md:w-7/12">
        {nonSubscribedProducts.length > 0 && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="typography-h3 text-primary font-medium">
                Add to plan
              </h2>
            </div>
            {nonSubscribedProducts.map((formula: FormulaProduct) => (
              <ProductCard
                key={formula.product_id}
                formula={formula}
                isSelected={true} // In this view, non-subscribed are shown as if to be added
                isEditable={false}
                cardStyle="default"
              />
            ))}
          </>
        )}
        {subscribedProducts.length > 0 && (
          <>
            <h2 className="typography-h3 text-primary font-medium mt-6 mb-4">
              Already in my plan
            </h2>
            {subscribedProducts.map((formula: FormulaProduct) => (
              <ProductCard
                key={formula.product_id}
                formula={formula}
                isSelected={true}
                isEditable={false}
                cardStyle="subscribedHighlight"
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

interface NotSubscribedViewProps {
  promotionData: { splitOne?: string; splitTwo?: string } | undefined;
  dynamicPricing: PriceInfo;
  ctaButtonLabel: string;
  selectedProductIds: Set<string>;
  nonSubscribedProducts: FormulaProduct[];
  products: FormulaProduct[]; // For fallback display logic
  handleCustomizeClick: () => void;
  isAnySubscribed: boolean;
  onCtaClick: () => void;
  isCtaLoading: boolean;
}

export const NotSubscribedView = ({
  promotionData,
  dynamicPricing,
  ctaButtonLabel,
  selectedProductIds,
  nonSubscribedProducts,
  products,
  handleCustomizeClick,
  isAnySubscribed,
  onCtaClick,
  isCtaLoading,
}: NotSubscribedViewProps) => {
  let productsToDisplay = nonSubscribedProducts;
  if (selectedProductIds.size > 0) {
    productsToDisplay = nonSubscribedProducts.filter((p) =>
      selectedProductIds.has(String(p.product_id)),
    );
  } else if (
    selectedProductIds.size === 0 &&
    nonSubscribedProducts.length === 0 &&
    products.length > 0
  ) {
    productsToDisplay = products;
  } else if (
    selectedProductIds.size === 0 &&
    nonSubscribedProducts.length > 0
  ) {
    productsToDisplay = nonSubscribedProducts;
  }

  const isSplitTwoOfferValid =
    promotionData && promotionData?.splitTwo && new Date(promotionData.splitTwo) > new Date();

  return (
    <div className="container flex flex-col md:flex-row gap-8 p-4">
      <div className="md:w-5/12">
        <div className="mb-4 flex items-center md:items-start flex-col">
          <div className="flex flex-nowrap max-w-fit backdrop-blur-[25px] mb-4">
            {promotionData?.splitOne && (
              <div
                className={`typography-caption text-primary-foreground py-2 px-4 bg-gradient-to-r from-[#57D3BD] to-[#038F76] ${
                  isSplitTwoOfferValid ? 'rounded-l-full' : 'rounded-full'
                }`}
              >
                {promotionData.splitOne}
              </div>
            )}
            {isSplitTwoOfferValid && (
              <div className="typography-btn-medium flex items-center text-background-success py-2 px-4 rounded-r-full bg-gradient-to-r from-[#B1EAE0] to-[#A2E6D7]">
                <OfferBannerWithTimer offerEndDate={promotionData.splitTwo} />
              </div>
            )}
          </div>
          <h1 className="typography-h1 text-primary mb-2">
            Precision Formulas
          </h1>
          <p className="typography-headline2 font-serif italic text-primary mb-4">
            Made-for-you
          </p>
          <div className="flex items-baseline gap-2 flex-wrap">
            <PriceDisplay priceInfo={dynamicPricing} />
            <MonthlyPriceDisplay priceInfo={dynamicPricing} />
          </div>
          <div className="fixed bottom-16 md:bottom-auto md:relative md:left-auto left-0 w-full flex justify-center md:block z-50">
            <Button
              className="rounded-md py-3 bg-black text-white hover:bg-opacity-90 typography-btn-medium md:min-w-0 min-w-[300px] px-24 md:px-5"
              onClick={onCtaClick}
              disabled={
                isCtaLoading ||
                (!dynamicPricing.isPriceAvailable &&
                  selectedProductIds.size === 0 &&
                  nonSubscribedProducts.length > 0)
              }
              data-testid="shopFormulas-orderButton"
            >
              {isCtaLoading ? 'Processing...' : ctaButtonLabel}
            </Button>
          </div>
        </div>
      </div>
      <div className="max-w-md mx-auto md:w-7/12">
        <div className="flex justify-between items-center mb-4">
          <h2 className="typography-h3 text-primary font-medium">
            Formulas included
          </h2>
          {!isAnySubscribed && nonSubscribedProducts.length > 0 && (
            <button
              onClick={handleCustomizeClick}
              className="flex items-center typography-body-sm text-[#038F76] hover:text-[#027B63]"
            >
              Customize <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          )}
        </div>
        {productsToDisplay.map((formula: FormulaProduct) => (
          <ProductCard
            key={formula.product_id}
            formula={formula}
            isSelected={
              selectedProductIds.size > 0
                ? selectedProductIds.has(String(formula.product_id))
                : true
            }
            isEditable={false}
            cardStyle="default"
          />
        ))}
      </div>
    </div>
  );
};
